import React, { useEffect, useState } from "react";
import { Plus, Sparkles, Image as ImageIcon, Layers, ArrowRight } from "lucide-react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import { track } from "../services/analytics.js";
import "./DecorProjects.css";

export default function DecorProjects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState("");
    const [newProjectDesc, setNewProjectDesc] = useState("");
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        let isMounted = true;

        async function loadProjects() {
            setLoading(true);
            setError("");
            try {
                const res = await fetch("/api/fast/projects");
                if (!res.ok) throw new Error("Erreur lors du chargement des projets.");

                const data = await res.json();
                const projectsList = Array.isArray(data.items) ? data.items : [];

                if (!isMounted) return;
                setProjects(projectsList);
            } catch (err) {
                if (!isMounted) return;
                setError(err.message || "Erreur inconnue.");
                setProjects([]);
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        loadProjects();

        return () => {
            isMounted = false;
        };
    }, []);

    function openCreateModal() {
        setNewProjectName("");
        setNewProjectDesc("");
        setShowCreateModal(true);
    }

    async function handleCreateProject() {
        const name = newProjectName.trim() || t("decor.projects.newProject");
        setCreating(true);
        try {
            const res = await fetch("/api/fast/projects", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name,
                    description: newProjectDesc.trim(),
                    type: "decor"
                })
            });

            if (!res.ok) throw new Error("Impossible de creer le projet.");

            const data = await res.json();
            const newProject = data.item;

            if (newProject?.project_id) {
                track("project_created", { project_id: newProject.project_id, type: "decor" });
                try {
                    await fetch("/api/fast/user/settings", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ active_project_id: newProject.project_id })
                    });
                    window.sessionStorage.setItem("contentos_active_project_id", newProject.project_id);
                } catch {
                    // ignore
                }
                window.location.href = "/decor";
            }
        } catch (err) {
            setError(err.message || "Erreur lors de la creation du projet.");
        } finally {
            setCreating(false);
            setShowCreateModal(false);
        }
    }

    async function handleProjectClick(project) {
        const projectId = project?.project_id || project?.id || "";
        if (!projectId) return;

        try {
            track("project_opened", { project_id: projectId, type: "decor" });
            // Set as active project
            await fetch("/api/fast/user/settings", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ active_project_id: projectId })
            });
            window.sessionStorage.setItem("contentos_active_project_id", projectId);

            // Redirect to editor
            window.location.href = "/decor";
        } catch (err) {
            setError(err.message || "Erreur lors de l'ouverture du projet.");
        }
    }

    async function handleArchiveProject(project, event) {
        event?.stopPropagation();
        const projectId = project?.project_id || project?.id || "";
        if (!projectId) return;
        if (!window.confirm("Archiver ce projet ?")) return;
        try {
            const res = await fetch("/api/fast/projects/archive", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ project_id: projectId })
            });
            if (!res.ok) throw new Error("Impossible d'archiver le projet.");
            setProjects((prev) => prev.filter((p) => (p.project_id || p.id) !== projectId));
        } catch (err) {
            setError(err.message || "Erreur lors de l'archivage.");
        }
    }

    async function handleDeleteProject(project, event) {
        event?.stopPropagation();
        const projectId = project?.project_id || project?.id || "";
        if (!projectId) return;
        if (!window.confirm("Supprimer definitivement ce projet ?")) return;
        try {
            const res = await fetch("/api/fast/projects/delete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ project_id: projectId })
            });
            if (!res.ok) throw new Error("Impossible de supprimer le projet.");
            setProjects((prev) => prev.filter((p) => (p.project_id || p.id) !== projectId));
        } catch (err) {
            setError(err.message || "Erreur lors de la suppression.");
        }
    }

    function handleOpenMedia(project, event) {
        event?.stopPropagation();
        const projectName = project?.name || "Sans projet";
        try {
            const payload = {
                sourceTab: "mine",
                typeFilter: "all",
                projectFilter: projectName,
                sortOrder: "newest"
            };
            window.sessionStorage.setItem("contentos_media_state", JSON.stringify(payload));
        } catch {
            // ignore
        }
        window.location.href = "/media";
    }

    function renderEmptyState() {
        return (
            <div className="decor-projects-empty">
                <div className="decor-projects-empty-content">
                    <h2 className="decor-projects-empty-title page-title-accent">{t("decor.projects.empty.title")}</h2>
                    <p className="decor-projects-empty-subtitle">{t("decor.projects.empty.subtitle")}</p>

                    <button className="decor-projects-create-cta" onClick={openCreateModal}>
                        <Plus size={20} />
                        {t("decor.projects.create")}
                    </button>

                    <div className="decor-projects-tutorial">
                        <h3 className="decor-projects-tutorial-title">{t("decor.projects.tutorial.title")}</h3>

                        <div className="decor-projects-tutorial-steps">
                            <div className="decor-projects-tutorial-step">
                                <div className="decor-projects-tutorial-step-icon">
                                    <ImageIcon size={24} />
                                </div>
                                <div className="decor-projects-tutorial-step-content">
                                    <h4>{t("decor.projects.tutorial.step1.title")}</h4>
                                    <p>{t("decor.projects.tutorial.step1.desc")}</p>
                                </div>
                            </div>

                            <div className="decor-projects-tutorial-step">
                                <div className="decor-projects-tutorial-step-icon">
                                    <Layers size={24} />
                                </div>
                                <div className="decor-projects-tutorial-step-content">
                                    <h4>{t("decor.projects.tutorial.step2.title")}</h4>
                                    <p>{t("decor.projects.tutorial.step2.desc")}</p>
                                </div>
                            </div>

                            <div className="decor-projects-tutorial-step">
                                <div className="decor-projects-tutorial-step-icon">
                                    <Sparkles size={24} />
                                </div>
                                <div className="decor-projects-tutorial-step-content">
                                    <h4>{t("decor.projects.tutorial.step3.title")}</h4>
                                    <p>{t("decor.projects.tutorial.step3.desc")}</p>
                                </div>
                            </div>
                        </div>

                        <div className="decor-projects-tutorial-features">
                            <h4>{t("decor.projects.tutorial.features.title")}</h4>
                            <ul>
                                <li>{t("decor.projects.tutorial.features.feat1")}</li>
                                <li>{t("decor.projects.tutorial.features.feat2")}</li>
                                <li>{t("decor.projects.tutorial.features.feat3")}</li>
                                <li>{t("decor.projects.tutorial.features.feat4")}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderProjects() {
        return (
            <div className="decor-projects-list">
                <div className="decor-projects-header">
                    <div>
                        <h1 className="decor-projects-title page-title-accent">{t("decor.projects.title")}</h1>
                        <p className="decor-projects-subtitle">{t("decor.projects.subtitle")}</p>
                    </div>
                </div>
                <div className="decor-projects-actionbar">
                    <button className="decor-projects-create-cta" onClick={openCreateModal}>
                        <Plus size={20} />
                        {t("decor.projects.create")}
                    </button>
                </div>

                <div className="decor-projects-grid">
                    {projects.map((project) => {
                        const thumbnail = project.thumbnail_url || project.last_image_url || "";
                        const name = project.name || t("decor.projects.untitled");
                        const updatedAt = project.updated_at || project.created_at || "";
                        const imageCount = project.image_count || 0;

                        return (
                            <div
                                key={project.project_id || project.id}
                                className="decor-project-card"
                                onClick={() => handleProjectClick(project)}
                            >
                                <div className="decor-project-card-thumbnail">
                                    {thumbnail ? (
                                        <img src={thumbnail} alt={name} />
                                    ) : (
                                        <div className="decor-project-card-placeholder">
                                            <ImageIcon size={48} />
                                        </div>
                                    )}
                                    <div className="decor-project-card-overlay">
                                        <ArrowRight size={24} />
                                    </div>
                                </div>

                                <div className="decor-project-card-content">
                                    <h3 className="decor-project-card-name">{name}</h3>
                                    <div className="decor-project-card-meta">
                                        <span className="decor-project-card-count">
                                            {imageCount} {imageCount === 1 ? t("decor.projects.image") : t("decor.projects.images")}
                                        </span>
                                        {updatedAt && (
                                            <span className="decor-project-card-date">
                                                {new Date(updatedAt).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    <div className="decor-project-card-actions">
                                        <button
                                            className="decor-project-action"
                                            type="button"
                                            onClick={(event) => handleOpenMedia(project, event)}
                                        >
                                            Medias
                                        </button>
                                        <button
                                            className="decor-project-action ghost"
                                            type="button"
                                            onClick={(event) => handleArchiveProject(project, event)}
                                        >
                                            Archiver
                                        </button>
                                        <button
                                            className="decor-project-action danger"
                                            type="button"
                                            onClick={(event) => handleDeleteProject(project, event)}
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="decor-projects-page">
            <Sidebar active="projects" />
            <main className="decor-projects-main app-main">
                {loading && (
                    <div className="decor-projects-loading">
                        <div className="decor-projects-spinner"></div>
                        <p>{t("common.loading")}</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="decor-projects-error">
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && projects.length === 0 && renderEmptyState()}
                {!loading && !error && projects.length > 0 && renderProjects()}

                {showCreateModal && (
                    <div className="decor-modal-backdrop" onClick={() => !creating && setShowCreateModal(false)}>
                        <div className="decor-modal" onClick={(e) => e.stopPropagation()}>
                            <h2 className="decor-modal-title">Nouveau projet</h2>
                            <label className="decor-modal-field">
                                <span>Nom du projet</span>
                                <input
                                    type="text"
                                    placeholder={t("decor.projects.newProject")}
                                    value={newProjectName}
                                    onChange={(e) => setNewProjectName(e.target.value)}
                                    autoFocus
                                    onKeyDown={(e) => { if (e.key === "Enter" && !creating) handleCreateProject(); }}
                                />
                            </label>
                            <label className="decor-modal-field">
                                <span>Description</span>
                                <textarea
                                    placeholder="Description du projet (optionnel)"
                                    value={newProjectDesc}
                                    onChange={(e) => setNewProjectDesc(e.target.value)}
                                    rows={3}
                                />
                            </label>
                            <div className="decor-modal-actions">
                                <button
                                    type="button"
                                    className="decor-modal-cancel"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={creating}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    className="decor-modal-confirm"
                                    onClick={handleCreateProject}
                                    disabled={creating}
                                >
                                    {creating ? "Creation..." : "Creer le projet"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
